/**
 * In-memory store of FE actions ServiceNow accepted (202) but hasn't finished processing.
 * Drives the "action processing…" overlay: getTicketByNumber stamps `pendingAction` while
 * an op is live; the op is released by a detected state change, the inbound SN webhook, or
 * the TTL below — whichever comes first.
 *
 * SINGLE-INSTANCE ONLY. This Map is process-local, so the action mutation (setPending), the
 * detail-page poll (getPending) and the webhook (clearPending) must all be served by the
 * same Node process. The smartteams-app v1 deployment runs a single replica, which is what
 * makes this safe. Scaling to >1 replica requires moving this to shared storage (e.g. a
 * Mongo collection with a TTL index) — the overlay would otherwise behave non-deterministically.
 */
interface PendingOp {
  action: string;
  preState: string;
  /** Substate before the action — lets us detect substate-only moves (en_route: ∅ → 8 On Route). */
  preSubstate: string;
  createdAt: number;
}

const ENTRY_TTL = 60_000;
const CLEANUP_INTERVAL = 30_000;

const pending = new Map<string, PendingOp>();

const timer = setInterval(() => {
  const now = Date.now();
  for (const [key, op] of pending) {
    if (now - op.createdAt > ENTRY_TTL) pending.delete(key);
  }
}, CLEANUP_INTERVAL);
if (typeof timer === 'object' && 'unref' in timer) timer.unref();

export function setPending(
  ticketNumber: string,
  action: string,
  preState: string,
  preSubstate: string,
): void {
  pending.set(ticketNumber, { action, preState, preSubstate, createdAt: Date.now() });
}

export function clearPending(ticketNumber: string): void {
  pending.delete(ticketNumber);
}

export function getPending(ticketNumber: string): PendingOp | undefined {
  const op = pending.get(ticketNumber);
  if (!op) return undefined;
  if (Date.now() - op.createdAt > ENTRY_TTL) {
    pending.delete(ticketNumber);
    return undefined;
  }
  return op;
}
